import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Filter, X, Grid, LayoutGrid } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', name: 'All', color: 'bg-muted' },
  { id: 'crochet', name: 'Crochet', color: 'bg-pastel-crochet/30' },
  { id: 'stickers', name: 'Stickers', color: 'bg-pastel-stickers/30' },
  { id: 'art', name: 'Art', color: 'bg-pastel-art/30' },
  { id: 'keyrings', name: 'Keyrings', color: 'bg-pastel-keyrings/30' }
];

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const selectedCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    fetchProducts();
  }, [searchParams]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const category = searchParams.get('category');
      const search = searchParams.get('search');
      
      if (category && category !== 'all') params.append('category', category);
      if (search) params.append('search', search);
      
      const res = await fetch(`${API}/products?${params.toString()}`);
      if (res.ok) {
        setProducts(await res.json());
      }
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    const newParams = new URLSearchParams(searchParams);
    if (categoryId === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', categoryId);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery.trim()) {
      newParams.set('search', searchQuery.trim());
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const clearSearch = () => {
    setSearchQuery('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="block uppercase tracking-[0.22em] text-xs md:text-sm font-bold text-text-tertiary mb-3" data-testid="browse-eyebrow">
            discover & commission
          </span>
          <h1 className="text-4xl md:text-5xl font-display font-extrabold tracking-tight lowercase mb-2">browse the work</h1>
          <p className="text-text-secondary">Unique handmade goods from neurodivergent creators</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-brutal"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button type="submit" className="lowercase font-display font-bold bg-chilli text-butter hover:bg-chilli-deep rounded-full px-6" data-testid="search-btn">
              search
            </Button>
          </form>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2.5 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`lowercase font-display font-bold px-6 py-2.5 rounded-full text-sm transition-all duration-200 active:scale-95
                ${selectedCategory === cat.id
                  ? 'bg-text-primary text-butter shadow-sm'
                  : 'border-2 border-butter-deep bg-transparent text-text-secondary hover:border-chilli hover:text-text-primary hover:bg-butter-mid'
                }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Active Filters */}
        {(searchParams.get('search') || searchParams.get('category')) && (
          <div className="flex items-center gap-2 mb-6 text-sm">
            <span className="text-muted-foreground">Active filters:</span>
            {searchParams.get('search') && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-pastel-art/30 border border-primary/10">
                Search: {searchParams.get('search')}
                <button onClick={clearSearch}><X className="w-3 h-3" /></button>
              </span>
            )}
            {searchParams.get('category') && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-pastel-stickers/30 border border-primary/10">
                {searchParams.get('category')}
                <button onClick={() => handleCategoryChange('all')}><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <LoadingSpinner text="Loading products..." />
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <Search className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-2xl font-display font-extrabold lowercase mb-2">no products found</h3>
            <p className="text-text-secondary mb-6">Try adjusting your search or filters</p>
            <Button onClick={() => setSearchParams({})} variant="outline" className="lowercase font-display font-bold rounded-full border-2 border-butter-deep">
              clear all filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
